import { type ClipWindow } from "../clipTrack";

// ════════════════════════════════════════════════════════════════════════
// ClipPresetBar — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The quick-clip preset row shown in clip mode: "Last 10s", "Last
// 30s", "15s around", "Next 30s", plus "Set IN/OUT here" and "Reset".
// Ports the standalone reference's preset bar.
//
// Presets are anchored to the current playhead (`playhead` seconds):
//   - before  → `[t - n, t]`
//   - after   → `[t, t + n]`
//   - around  → `[t - n/2, t + n/2]`
// all clamped to `[0, duration]`. The parent applies the resulting
// window to the clip composer + clip track.
//
// IN/OUT and Reset are thin pass-throughs to the parent's handlers so
// the keyboard shortcuts (I / O) and these buttons share one code
// path. Buttons reuse the elevated-surface + rule-border treatment
// already used across the rail — no new tokens.
// ════════════════════════════════════════════════════════════════════════

export type ClipPresetBarProps = {
  /** Current playhead position, in seconds. */
  playhead: number;
  /** Total media duration, in seconds. */
  duration: number;
  /** Apply a fully-formed window (the four presets call this). */
  onSetWindow: (window: ClipWindow) => void;
  /** Set the clip IN point at the current playhead. */
  onSetIn: () => void;
  /** Set the clip OUT point at the current playhead. */
  onSetOut: () => void;
  /** Park the playhead back on the clip's FIRST frame — where the
   * clip's freeze-frame telestration lives. Setting OUT keeps the
   * playhead on the out frame (you want to see what you're cutting
   * at), so this is the explicit way back to mark the clip up. */
  onGoToIn: () => void;
  /** Reset the clip window around the current playhead. */
  onReset: () => void;
};

const btnClass =
  "inline-flex items-center gap-1.5 rounded border border-rule bg-elevated " +
  "px-2.5 py-1.5 font-display text-[11px] font-semibold text-ink " +
  "whitespace-nowrap transition-colors hover:bg-field " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export function ClipPresetBar({
  playhead,
  duration,
  onSetWindow,
  onSetIn,
  onSetOut,
  onGoToIn,
  onReset,
}: ClipPresetBarProps) {
  const preset = (seconds: number, anchor: "before" | "after" | "around") => {
    const t = playhead;
    let start: number;
    let end: number;
    if (anchor === "before") {
      start = Math.max(0, t - seconds);
      end = t;
    } else if (anchor === "after") {
      start = t;
      end = Math.min(duration, t + seconds);
    } else {
      start = Math.max(0, t - seconds / 2);
      end = Math.min(duration, t + seconds / 2);
    }
    onSetWindow({ start, end });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="coach-review-clip-preset-bar"
    >
      <span className="mr-1 font-mono text-[10px] uppercase tracking-mono text-ink-muted">
        Quick clip
      </span>
      <button
        type="button"
        className={btnClass}
        onClick={() => preset(10, "before")}
        data-testid="clip-preset-last-10"
      >
        Last 10s
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={() => preset(30, "before")}
        data-testid="clip-preset-last-30"
      >
        Last 30s
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={() => preset(15, "around")}
        data-testid="clip-preset-around-15"
      >
        15s around
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={() => preset(30, "after")}
        data-testid="clip-preset-next-30"
      >
        Next 30s
      </button>
      <span className="mx-1 h-4 w-px bg-rule" aria-hidden="true" />
      <button
        type="button"
        className={btnClass}
        onClick={onSetIn}
        data-testid="clip-preset-set-in"
      >
        Set IN here
        <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
          I
        </kbd>
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={onSetOut}
        data-testid="clip-preset-set-out"
      >
        Set OUT here
        <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
          O
        </kbd>
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={onGoToIn}
        title="Park on the clip's first frame to telestrate it"
        data-testid="clip-preset-first-frame"
      >
        First frame
        <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
          [
        </kbd>
      </button>
      <button
        type="button"
        className={btnClass}
        onClick={onReset}
        data-testid="clip-preset-reset"
      >
        Reset range
      </button>
    </div>
  );
}
